import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import { makeStyles } from "@material-ui/core/styles";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import { MuiPickersUtilsProvider, KeyboardDateTimePicker } from "@material-ui/pickers";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import Divider from "@material-ui/core/Divider";

import clsx from "clsx";
import { defaultPasscodeTheme } from "../../../constants/passCodeUtils";
import { NAV_COMPANYS } from "../../../constants/constants";
import { requestDocumentNumbers, clearDocumentFilter, setDocumentFilter, setNavCompany } from "../../../actions/billing";
import LocalizedUtils, { moment } from "../../../utils/LocalizedUtils";


const useStyles = makeStyles(theme => ({
  ...defaultPasscodeTheme(theme),
  filterContainer: {
    display: "flex",
    flexWrap: "wrap",
    width: "100%",
  },
  buttonContainer: {
    marginTop: theme.spacing(1),
    width: "100%",
    display: "inline-flex",
  },
  spaceButtons: {
    flex: "1",
    width: "10px",
  },
  controlLabel: {
    padding: theme.spacing(1, 1, 1, 1),
  },
}));

const BillingDocumentSendingFilterComponent = ({
  filter,
  navCompany,
  requestInProgress,
  translate,
  requestFn,
  clearFilterFn,
  setNavCompany,
  setFilterFn,
}) => {
  const classes = useStyles();

  const handleDateChange = (name, date) => {
    setFilterFn({ ...filter, [name]: moment.utc(date) });
  };

  const handleTextChange = name => event => {
    setFilterFn({ ...filter, [name]: event.target.value });
  };
  
  const handleNavCompanyChange = event => {
    setNavCompany(event.target.value);
  };

  const handleChange = name => event => {
    setFilterFn({ ...filter, [name]: event.target.checked });
  };

  return (
    <div className={classes.filterContainer}>
      <MuiPickersUtilsProvider libInstance={moment} utils={LocalizedUtils}>
        <KeyboardDateTimePicker
          id="ledger-entry-start-time"
          label={translate.orderDateFrom}
          className={clsx(classes.textField, classes.lineExpander)}
          ampm={false}
          format="DD.MM.YYYY HH:mm:ss"
          value={filter.fromPostingDate}
          onChange={date => handleDateChange("fromPostingDate", date)}
        />
        <KeyboardDateTimePicker
          id="ledger-entry-end-time"
          label={translate.orderDateTo}
          className={clsx(classes.textField, classes.lineExpander)}
          ampm={false}
          format="DD.MM.YYYY HH:mm:ss"
          value={filter.toPostingDate}
          onChange={date => handleDateChange("toPostingDate", date)}
        />
      </MuiPickersUtilsProvider>
      <div className={classes.lineBreak} />
      <TextField
        id="ledger-entry-conference-id"
        label={translate.fromEntryNo}
        className={clsx(classes.textField, classes.lineExpander)}
        value={filter.fromEntryNo}
        onChange={handleTextChange("fromEntryNo")}
      />
      <TextField
        id="ledger-entry-conference-id"
        label={translate.toEntryNo}
        className={clsx(classes.textField, classes.lineExpander)}
        value={filter.toEntryNo}
        onChange={handleTextChange("toEntryNo")}
      />
      <div className={classes.lineBreak} />
      <FormControl className={clsx(classes.textField, classes.lineExpander)}>
        <InputLabel htmlFor="dispatch-comment-select">Versandbemerkung</InputLabel>
        <Select
          native
          value={filter.dispatchComment}
          onChange={handleTextChange("dispatchComment")}
          inputProps={{
            name: "dispatchComment",
            id: "dispatch-comment-select",
          }}
        >
          <option aria-label="None" value="" />
          <option value="ONHOLD">ONHOLD</option>
        </Select>
      </FormControl>
      <FormControlLabel
        className={clsx(classes.textField, classes.lineExpander, classes.controlLabel)}
        control={<Checkbox checked={filter.paperOnly} onChange={handleChange("paperOnly")} value="primary" />}
        label={translate.paperOnly}
      />
      <div className={classes.lineBreak} />
      <FormControl className={clsx(classes.textField, classes.lineExpander)}>
        <InputLabel htmlFor="nav-company-select">NAV company</InputLabel>
        <Select
          native
          value={navCompany}
          onChange={handleNavCompanyChange}
          inputProps={{
            name: "navCompany",
            id: "nav-company-select",
          }}
        >
          {NAV_COMPANYS.forEach(company => {
             return <option aria-label="None" value={company}>{company.text}</option> 
          })}
        </Select>
      </FormControl>
      <Divider />
      <div className={classes.buttonContainer}>
        <div className={classes.spaceButtons} />
        <Button size="small" onClick={() => clearFilterFn(filter)}>
          {translate.clearButtonFilter}
        </Button>
        <Button
          disabled={requestInProgress}
          variant="contained"
          size="small"
          color="primary"
          onClick={() => requestFn(navCompany, filter)}
        >
          {translate.searchButtonFilter}
        </Button>
      </div>
    </div>
  );
};

BillingDocumentSendingFilterComponent.propTypes = {
  filter: PropTypes.object.isRequired,
  navCompany: PropTypes.object.isRequired,
  setNavCompany: PropTypes.func.isRequired,
  requestFn: PropTypes.func.isRequired,
  clearFilterFn: PropTypes.func.isRequired,
  setFilterFn: PropTypes.func.isRequired,
  requestInProgress: PropTypes.bool.isRequired,
  translate: PropTypes.object.isRequired,
};


const mapStateToProps = state => ({
  filter: state.billing.documentFilter,
  navCompany: state.billing.navCompany,
  requestInProgress: state.billing.requestBillingInProgress,
  translate: state.ui.translate,
});

const mapDispatchToProps = dispatch => ({
  requestFn: (company, filter) => dispatch(requestDocumentNumbers(company.key, filter)),
  clearFilterFn: () => dispatch(clearDocumentFilter()),
  setFilterFn: filter => dispatch(setDocumentFilter(filter)),
  setNavCompany: company => dispatch(setNavCompany(company)),
});

const BillingDocumentSendingFilter = connect(
  mapStateToProps,
  mapDispatchToProps
)(BillingDocumentSendingFilterComponent);

export default BillingDocumentSendingFilter;
